import { Table, useMantineTheme } from '@mantine/core';
import { useState, useMemo } from 'react';
import { AfaBadge } from '../AfaBadge';
import { AfaMenu } from '../AfaMenu';
import { AfaMenuItemProps } from '../AfaMenu/AfaMenu';
import { AfaMessageAnime } from '../AfaMessageAnime';
import { AfaPagination } from '../AfaPagination';
import { AfaSectionLoader } from '../AfaLoading';
import { Pointer } from 'lucide-react';

export type AfaTableColumnType = 'text' | 'badge' | 'menu' | 'date';

export interface AfaTableColumn {
  key: string;
  title: string;
  type: AfaTableColumnType;
  width?: string | number;
  sortable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  badgeConfig?: {
    colorMap?: Record<string, 'yellow' | 'orange' | 'lilac' | 'red' | 'blue' | 'green'>;
    variant?: 'filled' | 'light';
    onClick?: any;
  };
  menuConfig?: {
    getItems: (row: any) => AfaMenuItemProps[];
    variant?: 'filled' | 'light' | 'outline';
  };
}

export interface AfaTableProps {
  columns: AfaTableColumn[];
  data: any[];
  withColumnHeaders?: boolean;
  withTableBorder?: boolean;
  highlightOnHover?: boolean;
  maxHeight?: string | number;
  className?: string;
  onRowClick?: (row: any, index: number) => void;
  tableProps?: any;
  emptyState?: {
    title: string;
    description?: string;
  };
  pagination?: {
    itemsPerPage?: number; // Por defecto será 15
    currentPage?: number;
    onPageChange?: (page: number) => void;
  };
  loading?: boolean;
  loadingMessage?: string;
}

export function AfaTable({
  columns,
  data,
  withColumnHeaders = true,
  withTableBorder = true,
  highlightOnHover = true,
  maxHeight,
  className,
  onRowClick,
  tableProps,
  emptyState,
  pagination,
  loading = false,
  loadingMessage = "Carregant dades...",
}: AfaTableProps) {
  const theme = useMantineTheme();

  // Estado interno para la página actual si no se proporciona externamente
  const [internalPage, setInternalPage] = useState(1);

  // Usar página externa si se proporciona, sino usar la interna
  const currentPage = pagination?.currentPage ?? internalPage;

  // Tamaño de página por defecto: 15
  const itemsPerPage = pagination?.itemsPerPage ?? 15;

  // Calcular datos paginados
  const { paginatedData, totalPages } = useMemo(() => {
    const total = Math.ceil(data.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = data.slice(startIndex, endIndex);

    return {
      paginatedData: paginated,
      totalPages: total
    };
  }, [data, currentPage, itemsPerPage]);

  // Manejar cambio de página
  const handlePageChange = (newPage: number) => {
    if (pagination?.onPageChange) {
      pagination.onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const renderCell = (column: AfaTableColumn, value: any, row: any) => {
    if (column.render) {
      return column.render(value, row);
    }

    switch (column.type) {
      case 'badge':
        // Si el valor está vacío, no mostrar badge
        if (!value || value === '') {
          return null;
        }

        if (column.badgeConfig?.onClick != null) {
          
          const parts = value.split('|');
          const text = parts.length > 1 ? parts[0] : '-';
          const estado = parts.length > 1 ? parts[1] : value;
          const badgeColor = column.badgeConfig?.colorMap?.[estado] || 'blue';
          const badgeVariant = column.badgeConfig?.variant || 'light';

          return (
            <AfaBadge
              style={{ cursor: 'pointer' }}
              onClick={() => column.badgeConfig?.onClick?.(value, row)}
              color={badgeColor}
              variant={badgeVariant}
            >
              {text}
            </AfaBadge>
          );
        }
        else {
          const badgeColor = column.badgeConfig?.colorMap?.[value] || 'blue';
          const badgeVariant = column.badgeConfig?.variant || 'light';
          return (
            <AfaBadge color={badgeColor} variant={badgeVariant}>
              {value}
            </AfaBadge>
          );
        }

      case 'menu':
        const menuItems = column.menuConfig?.getItems(row) || [];
        const menuVariant = column.menuConfig?.variant || 'filled';
        return (
          <AfaMenu
            variant={menuVariant}
            items={menuItems}
          />
        );

      case 'date':
        if (value instanceof Date) {
          return value.toLocaleDateString('ca-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        return value;

      case 'text':
      default:
        return value;
    }
  };

  // Si está cargando, mostrar loader
  if (loading) {
    return <AfaSectionLoader message={loadingMessage} />;
  }

  // Si no hay datos y se especificó un emptyState, mostrar AfaMessageAnime
  if (data.length === 0 && emptyState) {
    return (
      <AfaMessageAnime
        title={emptyState.title}
        {...(emptyState.description && { description: emptyState.description })}
      />
    );
  }

  const tableContent = (
    <Table
      {...tableProps}
      withTableBorder={withTableBorder}
      withColumnBorders={false}
      highlightOnHover={highlightOnHover}
      className={className}
    >
      {withColumnHeaders && (
        <Table.Thead>
          <Table.Tr>
            {columns.map((column) => (
              <Table.Th
                key={column.key}
                style={{ width: column.width, fontSize: theme.fontSizes.xs }}
              >
                {column.title}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
      )}

      <Table.Tbody>
        {paginatedData.map((row, rowIndex) => (
          <Table.Tr
            key={rowIndex}
            onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
            style={onRowClick ? { cursor: 'pointer' } : undefined}
          >
            {columns.map((column) => {
              // 🔧 Soporte para acceder a propiedades anidadas (dot notation)
              const getValueByPath = (obj: any, path: string) =>
                path.split('.').reduce((acc, part) => acc?.[part], obj);

              const value = getValueByPath(row, column.key);

              return (
                <Table.Td key={column.key} style={{ fontSize: theme.fontSizes.md }}>
                  {renderCell(column, value, row)}
                </Table.Td>
              );
            })}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );

  if (maxHeight) {
    return (
      <>
        <div style={{
          maxHeight,
          overflowY: 'auto',
          overflowX: 'auto',
          width: '100%'
        }}>
          <div style={{ minWidth: 'max-content' }}>
            {tableContent}
          </div>
        </div>
        {pagination && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
            <AfaPagination
              total={totalPages}
              value={currentPage}
              onChange={handlePageChange}
            />
          </div>
        )}
      </>
    );
  }

  // Siempre garantizar scroll horizontal
  return (
    <>
      <div style={{
        overflowX: 'auto',
        overflowY: 'visible',
        width: '100%'
      }}>
        <div style={{ minWidth: 'max-content' }}>
          {tableContent}
        </div>
      </div>
      {pagination && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <AfaPagination
            total={totalPages}
            value={currentPage}
            onChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
}

export default AfaTable;
